/**
 * sl-8fwi.8 (#64 fix): Pure helper for the manual `handleMappingChange`
 * writer path in `field-mapping-grid.tsx`. Extracted from the React
 * useCallback closure so the spec can drive the SAME function the
 * component uses — pre-fix the only test coverage was the auto-match
 * pipeline (`auto-match-fields-display-name.spec.ts`), which never
 * exercised what happens when the user explicitly picks (or clears) a
 * source field via the dropdown.
 *
 * SOLID:
 * - S: Single responsibility — compute the next mapping array given a
 *      single column-id update. No side effects, no React, no notify.
 * - D: All collaborators (display-name resolver, schema fields) are
 *      injected; the function knows nothing about WorkflowService or
 *      i18n.
 *
 * The component still owns the React state plumbing (useCallback,
 * onMappingsChange) — this helper only owns the mapping math.
 *
 * sl-8fwi rework (AC3 strict validation): Pre-rework the helper
 * persisted unknown response-field paths with a fallback display name
 * resolved via `getDisplayName`. The deep audit flagged this as a
 * silent-failure path — the dropdown should never offer a path outside
 * `responseFields`, so one arriving here is either stale state or a
 * bug, and persisting it hides the root cause. The helper now rejects
 * unknown paths outright: the existing mapping is still removed
 * (explicit clear), but no new mapping is appended. The `getDisplayName`
 * parameter is kept for API compatibility but is no longer consulted
 * on the unknown-path branch.
 */

import type { OutputMapping } from '../../services/automation';

/** Schema field shape used by FieldMappingGrid (mirrors the local
 *  `SchemaField` interface so we don't import the entire component). */
export interface OutputMappingSchemaField {
  readonly path: string;
  readonly displayName: string;
  readonly type?: string;
}

export interface ApplyMappingChangeInput {
  /** Current mapping array (will not be mutated). */
  readonly prev: ReadonlyArray<OutputMapping>;
  /** Target column whose mapping is being updated. */
  readonly columnId: string;
  /** New response field path. Empty string clears the mapping. */
  readonly responseField: string;
  /** Target table id — required to insert a new mapping. Without it
   *  the helper only removes the existing mapping (defensive: a
   *  mapping cannot be persisted without a table reference). */
  readonly tableId: string | undefined;
  /** Schema fields available in the workflow output catalogue. */
  readonly responseFields: ReadonlyArray<OutputMappingSchemaField>;
  /** Historical display-name resolver. Pre-rework this was consulted when
   *  a path was not in `responseFields`. After the sl-8fwi rework
   *  (AC3 strict validation) it is NEVER consulted for unknown paths
   *  because unknown paths are rejected — it survives as a parameter
   *  only so existing callers keep compiling. */
  readonly getDisplayName: (path: string) => string;
}

/**
 * Compute the next OutputMapping array after the user explicitly picked
 * (or cleared) a source field for `columnId`. Removes any existing
 * mapping for the column and, when `responseField` + `tableId` are both
 * present AND `responseField` resolves to a real entry in
 * `responseFields`, appends the new one.
 *
 * The new mapping carries:
 *   - `responseFieldName`: snapshot of the workflow output field's
 *     `displayName` so the canvas-block renderer (sl-g3on.4.1) can
 *     show a human label without re-fetching the schema.
 *   - `matchConfigId`: preserved from the existing mapping for the
 *     same column so the relation-lookup MatchConfig stays intact when
 *     the user just changes the source field.
 *   - `fixedValue`: cleared because picking a response field replaces
 *     any previously-configured fixed value.
 */
export function applyMappingChange(
  input: ApplyMappingChangeInput
): OutputMapping[] {
  const { prev, columnId, responseField, tableId, responseFields } = input;

  const existingMapping = prev.find(m => m.targetColumnId === columnId);
  const filteredMappings = prev.filter(m => m.targetColumnId !== columnId);

  if (!responseField || !tableId) {
    return filteredMappings;
  }

  // sl-8fwi.8 rework (AC3): strict validation — the path MUST exist in
  // the cached schema. Unknown paths are rejected outright so the UI
  // state (dropdown) and the persisted state (mapping array) can never
  // diverge.
  const fieldInfo = responseFields.find(f => f.path === responseField);
  if (!fieldInfo) {
    return filteredMappings;
  }

  filteredMappings.push({
    tableId,
    targetColumnId: columnId,
    responseField,
    responseFieldName: fieldInfo.displayName,
    responseFieldType: fieldInfo.type,
    matchConfigId: existingMapping?.matchConfigId,
    fixedValue: undefined,
  });

  return filteredMappings;
}
